import { getEtag, setEtag } from '../../utils/etag';

/**
 * Cross-cutting interceptor helpers shared by every generated `@hey-api` client.
 *
 * They keep the `utils/etag` map in sync for every client, so a mutation can
 * send back the ETag returned by the matching GET:
 *
 * - Response: capture the `etag` header into the shared map keyed by path.
 * - Request: attach `If-Match` from the map on mutating requests (PUT/POST/...).
 * - Error: surface the HTTP status on the thrown error so retry logic keeps
 *   working (the fetch client throws the parsed error body, not a typed
 *   exception with a `.status`).
 */

const MUTATING_METHODS = new Set(['PUT', 'POST', 'PATCH', 'DELETE']);

/**
 * Normalise a request URL to a stable `pathname + search` key so that values
 * stored from one client can be read back by another, regardless of whether
 * the URL was absolute or relative.
 */
function etagKey(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

export function captureEtagFromResponse(response: Response, request: Request): Response {
  const etag = response.headers.get('etag');
  if (etag) {
    setEtag(etagKey(request.url), etag);
  }
  return response;
}

export function attachIfMatchHeader(request: Request): Request {
  if (!MUTATING_METHODS.has(request.method.toUpperCase())) return request;
  if (request.headers.has('If-Match')) return request;

  const etag = getEtag(etagKey(request.url));
  if (!etag) return request;

  const headers = new Headers(request.headers);
  headers.set('If-Match', etag);
  return new Request(request, { headers });
}

export function attachStatusToError(error: unknown, response: Response | undefined): unknown {
  if (!response) return error;

  // Already carries a status — leave it alone.
  if (error && typeof error === 'object' && 'status' in error) return error;

  // Plain object: attach status in-place.
  if (error && typeof error === 'object') {
    try {
      (error as { status?: number }).status = response.status;
      return error;
    } catch {
      // Frozen object — fall through and wrap instead.
    }
  }

  // Non-object errors (string, number, null, undefined) and frozen objects
  // cannot carry .status directly. Wrap them so retry logic can always read
  // the HTTP status. This covers the common case where a non-ok @hey-api
  // response has an empty or plain-string body: the client throws the raw
  // text, which is a string, not an object.
  return { status: response.status, cause: error };
}
